import type {
  LoginResponseDto,
  VerifyOtpResponseDto,
  ResendOtpResponseDto,
  PrivacyPolicyResponseDto,
  TermsAndConditionsResponseDto,
  InvestorRiskPolicyResponseDto,
  UaePassAuthorizeUrlResponseDto,
  UaePassCallbackResponseDto,
  UaePassRegisterResponseDto,
  LogoutResponseDto,
  UaePassDisableResponseDto,
  LoginMethodsResponseDto,
} from './authContracts'
import {
  UAE_PASS_CALLBACK_OUTCOME,
  type LoginResponse,
  type VerifyOtpResponse,
  type ResendOtpResponse,
  type PrivacyPolicyResponse,
  type TermsAndConditionsResponse,
  type InvestorRiskPolicyResponse,
  type UaePassAuthorizeUrlResponse,
  type UaePassCallbackResponse,
  type UaePassCallbackOutcome,
  type UaePassSignupPrefill,
  type UaePassRegisterResponse,
  type LogoutResponse,
  type UaePassDisableResponse,
  type LoginMethodsResponse,
} from '../types/authTypes'

export const parseLoginResponse = (resp: LoginResponseDto): LoginResponse => ({
  message: resp.data?.message ?? resp.message ?? '',
  verificationId: resp.data?.verificationId ?? resp.data?.verification_id ?? '',
})

export const parseVerifyOtpResponse = (resp: VerifyOtpResponseDto): VerifyOtpResponse => ({
  message: resp.data?.message ?? resp.message ?? '',
  accessToken: resp.data?.accessToken ?? resp.data?.access_token ?? '',
  refreshToken: resp.data?.refreshToken ?? resp.data?.refresh_token ?? '',
})

export const parseResendOtpResponse = (resp: ResendOtpResponseDto): ResendOtpResponse => ({
  message: resp.data?.message ?? resp.message ?? '',
  verificationId: resp.data?.verificationId ?? resp.data?.verification_id ?? '',
})

export const parseUaePassAuthorizeUrlResponse = (
  resp: UaePassAuthorizeUrlResponseDto,
): UaePassAuthorizeUrlResponse => ({
  authorizeUrl: resp.data?.authorizeUrl ?? resp.data?.authorize_url ?? '',
})

const toCallbackOutcome = (value: string | undefined): UaePassCallbackOutcome => {
  // A linked-identity backend that omitted `outcome` but returned tokens is
  // treated as a login; otherwise fall back to the sign-up signal so the portal
  // never silently drops the user on an unrecognized value.
  if (value === UAE_PASS_CALLBACK_OUTCOME.LOGIN) return UAE_PASS_CALLBACK_OUTCOME.LOGIN
  if (value === UAE_PASS_CALLBACK_OUTCOME.EMAIL_EXISTS_USE_OTP)
    return UAE_PASS_CALLBACK_OUTCOME.EMAIL_EXISTS_USE_OTP
  if (value === UAE_PASS_CALLBACK_OUTCOME.LINKED) return UAE_PASS_CALLBACK_OUTCOME.LINKED
  return UAE_PASS_CALLBACK_OUTCOME.SIGNUP_REQUIRED
}

const toSignupPrefill = (data: UaePassCallbackResponseDto['data']): UaePassSignupPrefill | null => {
  if (!data?.prefill) return null
  const { prefill } = data
  return {
    firstName: prefill.firstName ?? '',
    lastName: prefill.lastName ?? '',
    email: prefill.email ?? '',
    mobile: prefill.mobile ?? '',
    hasEmiratesId: prefill.hasEmiratesId ?? false,
  }
}

export const parseUaePassCallbackResponse = (
  resp: UaePassCallbackResponseDto,
): UaePassCallbackResponse => {
  const accessToken = resp.data?.accessToken ?? resp.data?.access_token ?? ''
  const rawOutcome =
    resp.data?.outcome ?? (accessToken ? UAE_PASS_CALLBACK_OUTCOME.LOGIN : undefined)
  return {
    outcome: toCallbackOutcome(rawOutcome),
    message: resp.data?.message ?? resp.message ?? '',
    email: resp.data?.email ?? '',
    accessToken,
    refreshToken: resp.data?.refreshToken ?? resp.data?.refresh_token ?? '',
    signupTicket: resp.data?.signupTicket ?? resp.data?.signup_ticket ?? '',
    prefill: toSignupPrefill(resp.data),
  }
}

export const parseUaePassRegisterResponse = (
  resp: UaePassRegisterResponseDto,
): UaePassRegisterResponse => ({
  accessToken: resp.data?.accessToken ?? resp.data?.access_token ?? '',
  refreshToken: resp.data?.refreshToken ?? resp.data?.refresh_token ?? '',
  hasEmiratesId: resp.data?.hasEmiratesId ?? resp.data?.has_emirates_id ?? false,
})

export const parseLogoutResponse = (resp: LogoutResponseDto): LogoutResponse => ({
  uaePassLogoutUrl: resp.data?.uaePassLogoutUrl ?? resp.data?.uae_pass_logout_url ?? null,
})

export const parseUaePassDisableResponse = (
  resp: UaePassDisableResponseDto,
): UaePassDisableResponse => ({
  success: resp.data?.success ?? false,
})

export const parseLoginMethodsResponse = (resp: LoginMethodsResponseDto): LoginMethodsResponse => ({
  methods: (resp.data?.methods ?? [])
    .filter((entry) => Boolean(entry.method))
    .map((entry) => ({
      method: entry.method ?? '',
      verifiedAt: entry.verifiedAt ?? entry.verified_at ?? null,
    })),
})

export const parsePrivacyPolicyResponse = (
  resp: PrivacyPolicyResponseDto,
): PrivacyPolicyResponse => ({
  message: resp.message ?? '',
  content: resp.data?.content ?? '',
})

export const parseTermsAndConditionsResponse = (
  resp: TermsAndConditionsResponseDto,
): TermsAndConditionsResponse => ({
  message: resp.message ?? '',
  content: resp.data?.content ?? '',
})

export const parseInvestorRiskPolicyResponse = (
  resp: InvestorRiskPolicyResponseDto,
): InvestorRiskPolicyResponse => ({
  message: resp.message ?? '',
  content: resp.data?.content ?? '',
})
